/**
 * One connector in the sheet. The head carries its status and health; the
 * body says what it is, why it needs you, who can see its channels, what it
 * reads and writes, and folds Configure and History; the footer reads by
 * status (Disconnect / Reconnect, Dismiss / Add API key, Disconnect / Retry
 * sync, Disconnect / Sync now, Use API key / Connect, Request early access).
 * Verbs that leave for an OAuth consent screen are links, the way they have
 * always been, so the browser follows them.
 *
 * The sheet prints outside the tab's tree: everything comes in as props.
 */
import { useState, type ReactNode } from "react";
import type { ConnectedServiceCredentialFieldDefinition } from "@yagni/shared";
import { getDefaultConnectedServiceCapabilities } from "@yagni/shared";
import { GoogleScopeDisclosure } from "../components/GoogleBrand.js";
import { ConnectorContainersList } from "../integrations/ConnectorContainersList.js";
import {
  GOOGLE_WORKSPACE_ALL_SCOPES,
  googleWorkspaceInstallUrl,
  hubspotInstallUrl,
  oauthInstallUrl,
  oauthOpensDesktopApp,
  personalOauthInstallUrl,
} from "../lib/oauthInstallUrls.js";
import { Btn, Fold, MenuItem, MenuSeparator, Pill, RecordHead, Stamp, type PillKind } from "../queue/index.js";
import {
  CONNECTOR_VERB_LABEL,
  CONTAINER_BEARING_SERVICE_TYPES,
  STATUS_WORD,
  capabilityLabel,
  connectorFooter,
  healthLine,
  keyOf,
  latestSyncTimeAgo,
  type ConnectorRecord,
  type ConnectorVerb,
} from "./connectorsModel.js";
import { blankCredentials, filledCredentials, type Connections } from "./useConnections.js";
import { ReviewAppInstallNote } from "./ReviewAppInstallNote.js";

const RETURN_TO = "/services";

/** What a GitHub grant lets agents do. The marketing security page, the academy
 * and docs/GITHUB_APP_SETUP.md carry the same sentence; keep them in step. */
export const GITHUB_READ_WRITE_SENTENCE =
  "Agents can read any repository you grant the YAGNI app. They push only to the repository a ticket builds in.";

function pillKind(record: ConnectorRecord): PillKind {
  switch (record.status) {
    case "connected":
    case "personal":
    case "syncing":
      return "live";
    case "attention":
    case "stale":
      return "stop";
    case "pending":
    case "link":
      return "needs";
    default:
      return "done";
  }
}

/** Where a verb that leaves for a consent screen goes; null when it stays here. */
export function verbHref(record: ConnectorRecord, verb: ConnectorVerb): { href: string; newTab: boolean } | null {
  if (verb === "link_account" && record.kind === "link") {
    return { href: personalOauthInstallUrl(record.serviceType, RETURN_TO), newTab: false };
  }
  if (verb !== "connect" && verb !== "reconnect") return null;
  if (record.kind !== "service" && record.kind !== "template") return null;
  const { serviceType, definition } = record;
  if (serviceType === "google_workspace") return { href: googleWorkspaceInstallUrl(RETURN_TO), newTab: false };
  if (serviceType === "github" || definition.oauthFlow) {
    return { href: oauthInstallUrl(serviceType, RETURN_TO), newTab: oauthOpensDesktopApp(serviceType) };
  }
  return null;
}

function canAct(record: ConnectorRecord, verb: ConnectorVerb, canManage: boolean): boolean {
  if (verb === "request" || verb === "link_account") return true;
  if (record.kind === "service") {
    // A person's own grant is theirs to revoke (Google rows are always the viewer's own).
    const own = record.service.connectionScope === "personal" || record.serviceType === "google_workspace";
    if (own && (verb === "disconnect" || verb === "reconnect")) return true;
  }
  if (record.kind === "template" && record.serviceType === "google_workspace") return true;
  return canManage;
}

function footerWhy(record: ConnectorRecord, primary: ConnectorVerb | null, allowed: boolean): ReactNode {
  if (!allowed) {
    return record.kind === "template" ? "Ask a workspace admin to connect it." : "Ask a workspace admin to change this connection.";
  }
  switch (record.status) {
    case "attention":
      return (
        <>
          Reconnecting authorizes {record.name} again as you. <b>Nothing already synced is lost.</b>
        </>
      );
    case "pending":
      return (
        <>
          Adding the key finishes setup. <b>Stored encrypted, never shown again.</b>
        </>
      );
    case "stale":
      return <>Retrying reads everything since the last good sync.</>;
    case "personal":
      return <>Disconnecting stops attributing the writes YAGNI makes for you to your own account.</>;
    case "link":
      return (
        <>
          Connecting opens {record.name} to authorize as you. <b>Writes YAGNI makes for you are then yours.</b>
        </>
      );
    case "ready":
      return verbHref(record, "connect") ? (
        <>
          Connecting opens {record.name} to authorize. <b>Writes stay off until a Team needs them.</b>
        </>
      ) : (
        <>
          Connecting stores the key encrypted. <b>It is never shown again.</b>
        </>
      );
    case "early":
    case "wish":
      return (
        <>
          Requesting sends one note to YAGNI. <b>Nothing is connected until you connect it.</b>
        </>
      );
    default:
      return primary ? (
        <>
          Sync now reads changes since the last sync. <b>Disconnecting stops every Team that reads it.</b>
        </>
      ) : (
        <>
          <b>Disconnecting stops every Team that reads it.</b>
        </>
      );
  }
}

/** A footer verb: a link when it leaves for a consent screen, else a button. */
function FootVerb({
  verb,
  record,
  variant,
  label,
  disabled,
  onClick,
}: {
  verb: ConnectorVerb;
  record: ConnectorRecord;
  variant: "primary" | "quiet";
  label: string;
  disabled?: boolean;
  onClick: () => void;
}) {
  const link = verbHref(record, verb);
  if (link && !disabled) {
    return (
      <a
        className={`btn ${variant}`}
        href={link.href}
        {...(link.newTab ? { target: "_blank", rel: "noopener noreferrer" } : {})}
      >
        {label}
      </a>
    );
  }
  return (
    <Btn variant={variant} onClick={onClick} disabled={disabled}>
      {label}
    </Btn>
  );
}

function CredentialField({
  field,
  value,
  onChange,
  rotate,
}: {
  field: ConnectedServiceCredentialFieldDefinition;
  value: string;
  onChange: (value: string) => void;
  rotate?: boolean;
}) {
  const id = `cred-${field.key}`;
  return (
    <div className="field">
      <label htmlFor={id}>{rotate ? `Rotate ${field.label}` : field.label}</label>
      {field.inputType === "select" && field.options ? (
        <select id={id} value={value} onChange={(event) => onChange(event.target.value)}>
          <option value="">{field.placeholder}</option>
          {field.options.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      ) : (
        <div className={field.inputType === "password" ? "in mono" : "in"}>
          <input
            id={id}
            type={field.inputType}
            value={value}
            placeholder={rotate ? "Leave blank to keep the current one" : field.placeholder}
            onChange={(event) => onChange(event.target.value)}
          />
        </div>
      )}
      {field.helperText && <div className="help">{field.helperText}</div>}
    </div>
  );
}

function CapabilityChecklist({
  capabilities,
  chosen,
  onToggle,
}: {
  capabilities: readonly string[];
  chosen: readonly string[];
  onToggle: (capability: string) => void;
}) {
  if (capabilities.length === 0) return null;
  return (
    <div className="field top">
      <label>Capabilities</label>
      <div className="checklist">
        {capabilities.map((capability) => (
          <label key={capability}>
            <input type="checkbox" checked={chosen.includes(capability)} onChange={() => onToggle(capability)} />
            <span>{capabilityLabel(capability)}</span>
          </label>
        ))}
      </div>
    </div>
  );
}

function toggle(values: readonly string[], value: string): string[] {
  return values.includes(value) ? values.filter((v) => v !== value) : [...values, value];
}

function InboundWebhookUrl({ slug }: { slug: string }) {
  const [copied, setCopied] = useState(false);
  const url = `${window.location.origin}/api/webhooks/hook/${slug}`;
  return (
    <div className="field">
      <label htmlFor="inbound-webhook-url">Inbound webhook</label>
      <div className="inline">
        <div className="in mono">
          <input id="inbound-webhook-url" value={url} readOnly onFocus={(event) => event.currentTarget.select()} />
        </div>
        <Btn
          size="sm"
          variant="quiet"
          aria-label="Copy inbound webhook URL"
          onClick={() => {
            void navigator.clipboard?.writeText(url).then(
              () => {
                setCopied(true);
                window.setTimeout(() => setCopied(false), 1800);
              },
              () => {},
            );
          }}
        >
          {copied ? "Copied" : "Copy"}
        </Btn>
      </div>
      <div className="help">External services POST events here, signed with the secret below (HMAC-SHA256).</div>
    </div>
  );
}

export interface ConnectorSheetProps {
  record: ConnectorRecord;
  c: Connections;
  canManage: boolean;
  onClose: () => void;
  /** A record this one became (a template connected with a key). */
  onOpen: (recordId: string) => void;
}

export function ConnectorSheet({ record, c, canManage, onClose, onOpen }: ConnectorSheetProps) {
  const service = record.kind === "service" ? record.service : null;
  const definition = record.kind === "service" || record.kind === "template" || record.kind === "link" ? record.definition : null;
  const [configOpen, setConfigOpen] = useState(false);
  const [keyOpen, setKeyOpen] = useState(record.kind === "template" && !verbHref(record, "connect"));
  const [credentials, setCredentials] = useState<Record<string, string>>(() =>
    record.kind === "service" || record.kind === "template" ? blankCredentials(record.serviceType) : {},
  );
  const [webhookSecret, setWebhookSecret] = useState("");
  const [capabilities, setCapabilities] = useState<string[]>(() =>
    service ? service.capabilities : record.kind === "template" ? getDefaultConnectedServiceCapabilities(record.serviceType) : [],
  );
  const [apiKey, setApiKey] = useState("");
  const [sheetError, setSheetError] = useState<string | null>(null);

  const { secondary, primary } = connectorFooter(record);
  const primaryAllowed = primary ? canAct(record, primary, canManage) : false;
  const secondaryAllowed = secondary ? canAct(record, secondary, canManage) : false;
  const requested = record.kind === "request" && Boolean(c.requested[record.item.id]);
  const syncing = record.kind === "service" && record.statusRows.some((row) => c.busy[`retry:${row.source}`]);
  const queued = record.kind === "service" && record.statusRows.some((row) => c.retried[row.source]);

  const requiredFilled =
    definition != null &&
    definition.credentialFields.every((field) => !field.required || (credentials[field.key] ?? "").trim().length > 0);

  async function connectWithKey() {
    if (record.kind !== "template") return;
    const created = await c.connectService(record.serviceType, filledCredentials(credentials), webhookSecret, capabilities);
    if (created) onOpen(`service:${created.id}`);
  }

  function act(verb: ConnectorVerb) {
    setSheetError(null);
    switch (verb) {
      case "disconnect":
        if (service) void c.removeService(service.id).then((ok) => ok && onClose());
        return;
      case "reconnect":
        // A key-based connection reconnects by rotating its key.
        setConfigOpen(true);
        return;
      case "retry_sync":
      case "sync_now":
        if (record.kind !== "service") return;
        if (record.statusRows.length > 0) for (const row of record.statusRows) void c.retrySource(row.source);
        else if (record.serviceType === "github") void c.refreshService(record.service.id);
        return;
      case "dismiss":
        if (record.kind === "draft") void c.dismissDraft(record.draft.id).then((ok) => ok && onClose());
        return;
      case "add_key":
        if (record.kind !== "draft") return;
        c.finalizeDraft(record.draft, apiKey.trim())
          .then((created) => onOpen(`service:${created.id}`))
          .catch((err: unknown) => setSheetError(err instanceof Error ? err.message : "Unknown error"));
        return;
      case "use_key":
        setKeyOpen(true);
        return;
      case "connect":
        void connectWithKey();
        return;
      case "request":
        if (record.kind === "request") void c.requestIntegration(record.item.id);
        return;
      case "link_account":
        return;
    }
  }

  const primaryLabel = (verb: ConnectorVerb): string => {
    if (verb === "request") return requested ? "Requested" : record.status === "wish" ? "Request" : CONNECTOR_VERB_LABEL.request;
    if ((verb === "sync_now" || verb === "retry_sync") && queued) return "Queued";
    return CONNECTOR_VERB_LABEL[verb];
  };
  const primaryDisabled =
    (primary === "request" && (requested || Boolean(record.kind === "request" && c.busy[`request:${record.item.id}`]))) ||
    (primary === "add_key" && apiKey.trim().length === 0) ||
    (primary === "connect" && !verbHref(record, "connect") && (!requiredFilled || Boolean(record.kind === "template" && c.busy[`connect:${record.serviceType}`]))) ||
    ((primary === "sync_now" || primary === "retry_sync") && syncing);

  // The overflow verbs: what the old row and its Configure modal held.
  const moreItems: ReactNode[] = [];
  if (record.kind === "service" && record.service.connectionScope !== "personal") {
    if (record.serviceType === "github" && canManage) {
      moreItems.push(
        <MenuItem key="repos" onSelect={() => c.setRepoPickerOpen(true)}>
          Manage repos
        </MenuItem>,
        <MenuItem key="refresh" onSelect={() => void c.refreshService(record.service.id)}>
          Refresh the installation
        </MenuItem>,
      );
    }
    if (record.serviceType === "google_workspace") {
      moreItems.push(
        <a key="reauth" role="menuitem" href={googleWorkspaceInstallUrl(RETURN_TO)}>
          Re-authorize
        </a>,
      );
      if (record.service.isDefault !== false) {
        moreItems.push(
          <a key="add" role="menuitem" aria-label="Add another Google account" href={googleWorkspaceInstallUrl(RETURN_TO, { addAccount: true })}>
            Add another Google account
          </a>,
        );
      }
    }
    if (record.serviceType === "hubspot") {
      moreItems.push(
        <a key="writes" role="menuitem" aria-label="Enable HubSpot write access" href={hubspotInstallUrl(RETURN_TO, { writes: true })}>
          Enable write access
        </a>,
      );
    }
    if (canManage && record.serviceType !== "google_workspace") {
      moreItems.push(
        <MenuItem key="configure" onSelect={() => setConfigOpen(true)}>
          Configure
        </MenuItem>,
      );
      if (record.service.webhookSlug) {
        const url = `${window.location.origin}/api/webhooks/hook/${record.service.webhookSlug}`;
        moreItems.push(
          <MenuItem key="copy" onSelect={() => void navigator.clipboard?.writeText(url).catch(() => {})}>
            Copy inbound webhook URL
          </MenuItem>,
        );
      }
    }
    const reconnect = verbHref(record, "reconnect");
    if (reconnect && record.status !== "attention" && canAct(record, "reconnect", canManage) && record.serviceType !== "google_workspace") {
      moreItems.push(
        <a key="reconnect" role="menuitem" href={reconnect.href}>
          Reconnect
        </a>,
      );
    }
  }
  if (record.kind === "template" && record.serviceType === "github" && canManage) {
    moreItems.push(
      <a key="link" role="menuitem" href={oauthInstallUrl("github", RETURN_TO, { githubMode: "authorize" })}>
        Link existing installation
      </a>,
    );
  }
  if (record.kind === "service" && secondary === "disconnect" && secondaryAllowed && moreItems.length > 0) {
    moreItems.push(
      <MenuSeparator key="sep" />,
      <MenuItem key="disconnect" onSelect={() => act("disconnect")}>
        Disconnect
      </MenuItem>,
    );
  }

  const errorRows = record.kind === "service" ? record.statusRows.filter((row) => row.state !== "healthy" || row.lastSyncAt === null) : [];

  return (
    <>
      <RecordHead
        recordKey={keyOf(record).toUpperCase()}
        title={record.name}
        onClose={onClose}
        more={moreItems.length > 0 ? <>{moreItems}</> : undefined}
        meta={
          <>
            <Pill kind={pillKind(record)} dot={pillKind(record) !== "done"}>
              {requested ? "Requested" : STATUS_WORD[record.status]}
            </Pill>
            <span className="fact" style={{ marginLeft: "auto", fontSize: 11.5 }}>
              {healthLine(record, { repoCount: c.repoCount })}
            </span>
          </>
        }
      />
      <div className="sbody" data-testid="connector-sheet">
        <p className="body-p" style={{ color: "var(--text-primary)" }}>
          {record.kind === "request"
            ? record.item.description
            : record.kind === "draft"
              ? record.draft.description || `A custom ${record.draft.authScheme} connector @yagni drafted.`
              : definition?.description}
        </p>
        {service?.serviceType === "jira" && service.connectionScope !== "personal" && service.accountLabel && (
          <p className="body-p" data-testid="jira-site-label">
            Site: {service.accountLabel}
          </p>
        )}
        {service?.serviceType === "google_workspace" && service.accountEmail && (
          <p className="body-p">
            {service.accountLabel ? `${service.accountLabel} (${service.accountEmail})` : service.accountEmail}
            {service.isDefault ? " · Default" : ""}
          </p>
        )}
        {service?.customConfig && (
          <p className="body-p mono">
            {service.customConfig.baseUrl}, {service.customConfig.authScheme} auth
          </p>
        )}
        {record.kind === "request" && (
          <p className="body-p">
            {record.status === "early"
              ? `${record.name} is built but not yet open to every workspace. Requesting it puts this workspace first in line.`
              : `${record.name} is not built yet. Requests decide what is built next.`}
          </p>
        )}

        {record.status === "attention" && errorRows.length > 0 && (
          <div className="stopbox" data-testid="connector-attention">
            <div className="h">
              <Stamp>attention</Stamp>
              Why it needs you
            </div>
            {errorRows.map((row) => (
              <p key={row.source}>
                <b>{row.label}:</b> {row.state === "error" && row.lastError ? row.lastError.message : "Waiting on the first sync."}
              </p>
            ))}
            <p>Reconnecting re-authorizes as you; nothing already synced is lost.</p>
          </div>
        )}
        {record.status === "stale" && (
          <div className="blk">
            <div className="bh">
              <span className="label">Sync</span>
            </div>
            <div className="checks">
              {record.kind === "service" &&
                record.statusRows.map((row) => (
                  <div key={row.source}>
                    <span className="note">{row.state}</span>
                    <span>
                      {row.label}: {row.minutesBehind !== null ? `${row.minutesBehind} min behind` : "behind"}
                    </span>
                  </div>
                ))}
            </div>
          </div>
        )}

        {record.kind === "draft" && (
          <>
            <dl className="receipt">
              <dt>Base URL</dt>
              <dd className="mono">{record.draft.baseUrl}</dd>
              <dt>Auth</dt>
              <dd>
                {record.draft.authScheme}
                {record.draft.authParam ? ` (${record.draft.authParam})` : ""}
              </dd>
              {record.draft.apiKeyUrl && (
                <>
                  <dt>Get a key</dt>
                  <dd>
                    <a href={record.draft.apiKeyUrl} target="_blank" rel="noopener noreferrer">
                      {record.draft.apiKeyUrl}
                    </a>
                  </dd>
                </>
              )}
            </dl>
            {canManage && (
              <div className="field">
                <label htmlFor="draft-api-key">API key</label>
                <div className="in mono">
                  <input
                    id="draft-api-key"
                    type="password"
                    value={apiKey}
                    placeholder="Stored encrypted, never shown to agents"
                    onChange={(event) => setApiKey(event.target.value)}
                  />
                </div>
              </div>
            )}
          </>
        )}

        {record.kind === "service" && CONTAINER_BEARING_SERVICE_TYPES.has(record.serviceType) && record.service.connectionScope !== "personal" && (
          <div className="blk">
            <div className="bh">
              <span className="label">{record.serviceType === "slack" ? "Channel visibility" : "Who can see what"}</span>
              <span className="fact">dark by default</span>
            </div>
            <ConnectorContainersList source={record.serviceType} />
          </div>
        )}
        {record.kind === "service" && record.serviceType === "github" && (
          <div className="blk">
            <div className="bh">
              <span className="label">Repositories</span>
              <span className="fact">{c.repoCount == null ? "reading" : `${c.repoCount} granted`}</span>
            </div>
            {/* The installation's repository selection is the read boundary, so
                the admin choosing it should see what the grant means for agents. */}
            <div className="help" data-testid="github-read-write">{GITHUB_READ_WRITE_SENTENCE}</div>
            <ReviewAppInstallNote repos={null} variant="help" testId="github-review-app-note" />
            {canManage && (
              <div>
                <Btn size="sm" variant="secondary" onClick={() => c.setRepoPickerOpen(true)}>
                  Manage repos
                </Btn>
              </div>
            )}
          </div>
        )}

        {record.kind === "template" && (keyOpen || !verbHref(record, "connect")) && canAct(record, "connect", canManage) && definition && definition.credentialFields.length > 0 && (
          <div className="blk" data-testid="connector-key-form">
            <div className="bh">
              <span className="label">{verbHref(record, "connect") ? "Use an API key instead" : "Credentials"}</span>
            </div>
            {definition.credentialFields.map((field) => (
              <CredentialField
                key={field.key}
                field={field}
                value={credentials[field.key] ?? ""}
                onChange={(value) => setCredentials((current) => ({ ...current, [field.key]: value }))}
              />
            ))}
            <div className="field">
              <label htmlFor="new-webhook-secret">Webhook signing secret</label>
              <div className="in mono">
                <input
                  id="new-webhook-secret"
                  type="password"
                  value={webhookSecret}
                  placeholder="Optional"
                  onChange={(event) => setWebhookSecret(event.target.value)}
                />
              </div>
            </div>
            <CapabilityChecklist
              capabilities={definition.capabilities}
              chosen={capabilities}
              onToggle={(capability) => setCapabilities((current) => toggle(current, capability))}
            />
            {verbHref(record, "connect") && (
              <div className="acts" style={{ display: "flex", justifyContent: "flex-end" }}>
                <Btn size="sm" variant="secondary" disabled={!requiredFilled} onClick={() => void connectWithKey()}>
                  Connect with this key
                </Btn>
              </div>
            )}
          </div>
        )}
        {record.kind === "template" && record.serviceType === "google_workspace" && (
          <GoogleScopeDisclosure scopeString={GOOGLE_WORKSPACE_ALL_SCOPES} defaultOpen={false} />
        )}

        {(c.error || sheetError) && (
          <p className="err-line" role="alert">
            {sheetError ?? c.error}
          </p>
        )}

        <div>
          {definition && definition.capabilities.length > 0 && record.kind !== "template" && (
            <Fold title="What it reads and writes" count={definition.capabilities.length}>
              <div className="checks">
                {definition.capabilities.map((capability) => (
                  <div key={capability}>
                    <span className={service && service.capabilities.includes(capability) ? "ok" : "note"} style={{ minWidth: 28 }}>
                      {service && service.capabilities.includes(capability) ? "on" : "off"}
                    </span>
                    <span>{capabilityLabel(capability)}</span>
                  </div>
                ))}
              </div>
            </Fold>
          )}
          {record.kind === "service" && record.serviceType === "google_workspace" && (
            <Fold title="Scopes">
              <GoogleScopeDisclosure scopeString={GOOGLE_WORKSPACE_ALL_SCOPES} defaultOpen />
            </Fold>
          )}
          {record.kind === "service" && canManage && record.service.connectionScope !== "personal" && record.serviceType !== "google_workspace" && (
            <ConfigureFold
              key={record.service.id}
              record={record}
              open={configOpen}
              onToggle={setConfigOpen}
              credentials={credentials}
              setCredentials={setCredentials}
              webhookSecret={webhookSecret}
              setWebhookSecret={setWebhookSecret}
              capabilities={capabilities}
              setCapabilities={setCapabilities}
              saving={Boolean(c.busy[`save:${record.service.id}`])}
              onSave={() =>
                void c
                  .saveService(record.service, { capabilities, credentials, webhookSecret })
                  .then((ok) => {
                    if (!ok) return;
                    setCredentials(blankCredentials(record.serviceType));
                    setWebhookSecret("");
                  })
              }
            />
          )}
          {record.kind === "service" && (
            <Fold title="History">
              <p className="body-p">
                Connected {new Date(record.service.createdAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}.{" "}
                {record.statusRows.length > 0 ? `Last sync ${latestSyncTimeAgo(record.statusRows)}.` : ""}
              </p>
            </Fold>
          )}
        </div>
      </div>
      <div className="sfoot">
        <span className="why">{footerWhy(record, primary, primary ? primaryAllowed : secondaryAllowed)}</span>
        <span className="verbs">
          {secondary && secondaryAllowed && (
            <FootVerb
              verb={secondary}
              record={record}
              variant="quiet"
              label={secondary === "disconnect" && service && c.busy[`remove:${service.id}`] ? "Disconnecting…" : CONNECTOR_VERB_LABEL[secondary]}
              disabled={secondary === "disconnect" && Boolean(service && c.busy[`remove:${service.id}`])}
              onClick={() => act(secondary)}
            />
          )}
          {primary && primaryAllowed && (
            <FootVerb
              verb={primary}
              record={record}
              variant="primary"
              label={primaryLabel(primary)}
              disabled={primaryDisabled}
              onClick={() => act(primary)}
            />
          )}
        </span>
      </div>
    </>
  );
}

function ConfigureFold({
  record,
  open,
  onToggle,
  credentials,
  setCredentials,
  webhookSecret,
  setWebhookSecret,
  capabilities,
  setCapabilities,
  saving,
  onSave,
}: {
  record: Extract<ConnectorRecord, { kind: "service" }>;
  open: boolean;
  onToggle: (open: boolean) => void;
  credentials: Record<string, string>;
  setCredentials: (update: (current: Record<string, string>) => Record<string, string>) => void;
  webhookSecret: string;
  setWebhookSecret: (value: string) => void;
  capabilities: string[];
  setCapabilities: (update: (current: string[]) => string[]) => void;
  saving: boolean;
  onSave: () => void;
}) {
  // A custom connection rotates only its secret fields.
  const fields =
    record.serviceType === "custom"
      ? record.definition.credentialFields.filter((field) => field.inputType === "password")
      : record.definition.credentialFields;
  return (
    <details
      className="fold"
      open={open}
      onToggle={(event) => {
        const next = (event.currentTarget as HTMLDetailsElement).open;
        if (next !== open) onToggle(next);
      }}
      data-testid="connector-configure"
    >
      <summary>
        <svg className="chev" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden>
          <path d="M6 4l4 4-4 4" />
        </svg>
        Configure
        <span className="n" />
      </summary>
      <div className="fb" style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {record.service.webhookSlug && <InboundWebhookUrl slug={record.service.webhookSlug} />}
        <div className="field">
          <label htmlFor="webhook-secret">Signing secret</label>
          <div className="in mono">
            <input
              id="webhook-secret"
              type="password"
              value={webhookSecret}
              placeholder="Paste a new secret to rotate"
              onChange={(event) => setWebhookSecret(event.target.value)}
            />
          </div>
          <div className="help">Leave blank to keep the current secret. Rotating invalidates the previous signature.</div>
        </div>
        {fields.map((field) => (
          <CredentialField
            key={field.key}
            field={field}
            rotate
            value={credentials[field.key] ?? ""}
            onChange={(value) => setCredentials((current) => ({ ...current, [field.key]: value }))}
          />
        ))}
        <CapabilityChecklist
          capabilities={record.definition.capabilities}
          chosen={capabilities}
          onToggle={(capability) => setCapabilities((current) => toggle(current, capability))}
        />
        <div className="acts" style={{ display: "flex", justifyContent: "flex-end" }}>
          <Btn size="sm" variant="secondary" disabled={saving} onClick={onSave}>
            {saving ? "Saving…" : "Save"}
          </Btn>
        </div>
      </div>
    </details>
  );
}
